import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, UsersIcon, BuildingIcon, FilterIcon, XIcon, MapIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { AGE_BRACKETS, DEPARTMENTS, RESIDENCE_OPTIONS, type Coord } from '../../data/communications';
import { countActiveFilters, getPlacesBase, summarizePlaces } from '../../utils/audience';
import { AudienceMap } from './AudienceMap';
import { AudiencePlaces } from './AudiencePlaces';
import { AudienceFilters } from './AudienceFilters';
import type { AudiencePlace, AudienceState } from '../../types/composer';
interface AudienceSelectorProps {
  audience: AudienceState;
  onChange: (patch: Partial<AudienceState>) => void;
  audienceTotal: number;
}

/**
 * Audience = Lieu (whole city, or places that add up) × Qui (filters that
 * narrow it down). The summary bar spells out the combination.
 */
export function AudienceSelector({
  audience,
  onChange,
  audienceTotal
}: AudienceSelectorProps) {
  const {
    t,
    lang
  } = useLanguage();
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const [drawing, setDrawing] = useState(false);
  const [draftPoints, setDraftPoints] = useState<Coord[]>([]);
  const [showMap, setShowMap] = useState(false);
  const {
    scope,
    places
  } = audience;
  const filtersCount = countActiveFilters(audience);
  const setPlaces = (next: AudiencePlace[]) => onChange({
    places: next
  });
  const toggleSector = (sectorId: string) => {
    const existing = places.find(p => p.kind === 'sector' && p.sectorId === sectorId);
    setPlaces(existing ? places.filter(p => p.id !== existing.id) : [...places, {
      id: `pl-${sectorId}-${Date.now()}`,
      kind: 'sector',
      sectorId
    }]);
  };
  const finishDrawing = () => {
    if (draftPoints.length >= 3) {
      setPlaces([...places, {
        id: `pl-area-${Date.now()}`,
        kind: 'area',
        points: draftPoints
      }]);
    }
    setDraftPoints([]);
    setDrawing(false);
  };
  const cancelDrawing = () => {
    setDraftPoints([]);
    setDrawing(false);
  };

  // Filter chips shown in the summary, each removable in one click.
  const filterChips: {
    key: string;
    label: string;
    clear: () => void;
  }[] = [...(audience.selectedAges.length > 0 ? [{
    key: 'age',
    label: AGE_BRACKETS.filter(a => audience.selectedAges.includes(a.id)).map(a => a.label[lang]).join(', '),
    clear: () => onChange({
      selectedAges: []
    })
  }] : []), ...(audience.selectedDepartments.length > 0 ? [{
    key: 'dep',
    label: DEPARTMENTS.filter(d => audience.selectedDepartments.includes(d.id)).map(d => d.label[lang]).join(', '),
    clear: () => onChange({
      selectedDepartments: []
    })
  }] : []), ...(audience.residence !== 'all' ? [{
    key: 'res',
    label: RESIDENCE_OPTIONS.find(r => r.value === audience.residence)?.label[lang] ?? '',
    clear: () => onChange({
      residence: 'all'
    })
  }] : [])];
  const whereLabel = scope === 'all' ? t('Toute la ville', 'Entire city') : places.length === 0 ? t('Aucun lieu', 'No place') : summarizePlaces(places, lang);
  const scopeOptions: {
    id: AudienceState['scope'];
    icon: typeof UsersIcon;
    label: string;
    hint: string;
  }[] = [{
    id: 'all',
    icon: BuildingIcon,
    label: t('Toute la ville', 'Entire city'),
    hint: t('Tous les citoyens inscrits', 'Every registered citizen')
  }, {
    id: 'places',
    icon: MapPinIcon,
    label: t('Lieux précis', 'Specific places'),
    hint: t('Secteurs, adresses, rayons ou zones', 'Districts, addresses, radii or areas')
  }];
  return <section className="space-y-6" data-id="element-3836" data-mp="2xtdabpd1czg5">
      <h2 className="text-xl font-semibold text-slate-900" data-id="element-3837" data-mp="2xuog1uq1kh9w">{t('Audience', 'Audience')}</h2>

      <div className="bg-white rounded-xl border-[0.5px] border-slate-200 divide-y divide-slate-100" data-id="element-3838" data-mp="198u3i8bhsb7h">
        {/* 1 — WHERE */}
        <div className="p-6 space-y-4" data-id="element-3839" data-mp="1581thcusq2a5">
          <div className="flex items-baseline gap-2" data-id="element-3840" data-mp="105sxs8qjgm4c">
            <span className="text-xs font-bold text-[#4F46E5] bg-indigo-50 w-5 h-5 rounded-full inline-flex items-center justify-center" data-id="element-3841" data-mp="108ffh1758i0a">
              1
            </span>
            <h3 className="text-sm font-semibold text-slate-900" data-id="element-3842" data-mp="1suqg2bur2ix3">{t('Lieu', 'Place')}</h3>

          </div>

          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" role="radiogroup" aria-label={t('Lieu', 'Place')} data-id="element-3843" data-mp="1nwymzeidcqrb">
            {scopeOptions.map(o => {
            const Icon = o.icon;
            const active = scope === o.id;
            return <button key={o.id} type="button" role="radio" aria-checked={active} title={o.hint} onClick={() => {
              if (o.id === 'all') cancelDrawing();
              onChange({
                scope: o.id
              });
            }} className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${active ? 'bg-white text-[#4F46E5] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3844" data-mp="26v17bfzz1zis">
                  <Icon className="w-4 h-4" data-id="element-3845" data-mp="323uxblh5sjdy" />
                  {o.label}
                </button>;
          })}
          </div>

          <AnimatePresence initial={false} data-id="element-3846" data-mp="36vd26kmaz447">
            {scope === 'places' && <motion.div key="places" initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.2
          }} className="overflow-hidden" data-id="element-3847" data-mp="3bswfnib6fcv3">
                <div className="pt-1 space-y-4" data-id="element-3848" data-mp="1lqe2w446s3fx">
                  <AudiencePlaces places={places} onChange={setPlaces} onToggleSector={toggleSector} drawing={drawing} draftPoints={draftPoints} onStartDrawing={() => {
                setDraftPoints([]);
                setDrawing(true);
              }} onCancelDrawing={cancelDrawing} onFinishDrawing={finishDrawing} data-id="element-3849" data-mp="1gr1al3ujq45y" />
                  {(places.length > 0 || drawing) && <div className="space-y-2" data-id="element-3850" data-mp="2deplb0sa2ufh">
                      {!drawing && <button type="button" onClick={() => setShowMap(s => !s)} aria-expanded={showMap} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#4F46E5] transition-colors" data-id="element-3851" data-mp="29dccchtw2b4c">
                          <MapIcon className="w-3.5 h-3.5" data-id="element-3852" data-mp="8y12q7w8js5c" />
                          {showMap ? t('Masquer la carte', 'Hide map') : t('Voir sur la carte', 'Show on map')}
                          <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${showMap ? 'rotate-180' : ''}`} data-id="element-3853" data-mp="6iqg82etwhj" />
                        </button>}
                      {(showMap || drawing) && <AudienceMap places={places} onToggleSector={toggleSector} drawing={drawing} draftPoints={draftPoints} onAddDraftPoint={c => setDraftPoints(prev => [...prev, c])} data-id="element-3854" data-mp="38e7i821r1ztv" />}
                    </div>}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* 2 — WHO */}
        <div className="p-6 space-y-4" data-id="element-3855" data-mp="2f8bcju73vbbt">
          <div className="flex items-baseline gap-2" data-id="element-3856" data-mp="2f9d02y5b5qaf">
            <span className="text-xs font-bold text-[#4F46E5] bg-indigo-50 w-5 h-5 rounded-full inline-flex items-center justify-center" data-id="element-3857" data-mp="2afs2h77mmybr">
              2
            </span>
            <h3 className="text-sm font-semibold text-slate-900" data-id="element-3858" data-mp="21flqjprku6ep">{t('Qui', 'Who')}</h3>
            <span className="text-xs text-slate-400" data-id="element-3859" data-mp="1wgsapqoti3bd">{t('Facultatif', 'Optional')}</span>
          </div>
          <AudienceFilters audience={audience} onChange={onChange} data-id="element-3860" data-mp="1rhpij6txrrkt" />
        </div>

        {/* Summary */}
        <div className="px-6 py-4 bg-slate-50/70 rounded-b-xl flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-id="element-3861" data-mp="1ngdomp414k6t">
          <div className="flex flex-wrap items-center gap-1.5 text-sm text-slate-600" data-id="element-3862" data-mp="3lup2fc5cn7xw">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-slate-200 font-medium text-slate-700" data-id="element-3863" data-mp="3htvewjvtjv5j">
              <MapPinIcon className="w-3.5 h-3.5 text-[#4F46E5]" data-id="element-3864" data-mp="13k9m084xua7v" />
              {whereLabel}
              {scope === 'places' && places.length > 0 && <span className="text-xs text-slate-400 font-normal" data-id="element-3865" data-mp="1dd8azr4ck2s0">
                  {getPlacesBase(audience).toLocaleString(locale)}
                </span>}
            </span>
            {filterChips.map(chip => <React.Fragment key={chip.key} data-id="element-3866" data-mp="kpckj45df73o">
                <span className="text-xs font-semibold uppercase text-slate-400 px-0.5" data-id="element-3867" data-mp="pm2x3hhyvkkc">
                  {t('et', 'and')}
                </span>
                <span className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-md bg-white border border-slate-200 font-medium text-slate-700 max-w-[260px]" data-id="element-3868" data-mp="tmv7wf6hlxiq">
                  <FilterIcon className="w-3.5 h-3.5 text-[#4F46E5] flex-shrink-0" data-id="element-3869" data-mp="252bidfrj5t8v" />
                  <span className="truncate" data-id="element-3870" data-mp="2a1n12ubgj8t3">{chip.label}</span>
                  <button type="button" onClick={chip.clear} aria-label={`${t('Retirer le filtre', 'Remove filter')} ${chip.label}`} className="p-0.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-id="element-3871" data-mp="2a4988sufrmas">
                    <XIcon className="w-3 h-3" data-id="element-3872" data-mp="1l3nh0i94in3e" />
                  </button>
                </span>
              </React.Fragment>)}
            {filtersCount === 0 && <span className="text-xs text-slate-400 ml-1" data-id="element-3873" data-mp="nihn3bpxbf88">
                {t('· aucun filtre', '· no filter')}
              </span>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-3874" data-mp="xdjeyfbl2vei">
            <UsersIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-3875" data-mp="11gfprjuvtn7w" />
            <span className="text-sm text-slate-500" data-id="element-3876" data-mp="11dtillbwl9q7">
              {audienceTotal > 0 && filtersCount > 0 ? t('environ', 'about') + ' ' : ''}
              <span className="text-lg font-bold text-[#0F172A]" data-id="element-3877" data-mp="2cubg7or8lief">
                {audienceTotal.toLocaleString(locale)}
              </span>{' '}
              {t('personnes ciblées', 'people targeted')}
            </span>
          </div>
        </div>
      </div>
    </section>;
}